/*
*+------------------------------------------------------------------------+
*| Licensed Materials - Property of IBM
*| IBM Cognos Products: BI Dashboard
*| (C) Copyright IBM Corp. 2019
*|
*| US Government Users Restricted Rights - Use, duplication or disclosure
*| restricted by GSA ADP Schedule Contract with IBM Corp.
*+------------------------------------------------------------------------+
*/
define([
	'../../../../visualizations/vipr/VIPRUtils',
	'underscore'
], (VIPRUtils, _) => {

	/**
	 * INTENT: LocalAggregateSortInfo is used to construct a local aggregrate sort info object.
	 * This sort info is used when performing local client side sort processing.
	 */
	class LocalAggregateSortInfo {

		/**
		 * @Constructor
		 * @param {Object} visualization
		 */
		constructor(visualization) {
			this.visualization = visualization;
		}

		/**
		 * @return {Object} Returns an object that contains local aggregate sort details
		 * or null if it doesn't support local client sort.
		 */
		getSortInfo() {
			if (!this._doesVisualizationSupportOperation()) {
				return null;
			}

			return this._getAggregateSortInfo();
		}

		/**
		 * @return {Boolean} Returns true if the visualization supports local aggregate sort
		 */
		_doesVisualizationSupportOperation() {
			return VIPRUtils.aggregateAndSortOnClient(this.visualization.getDefinition().getId());
		}

		/**
		 * @param {Object} mappedSlot The mapped slot to obtain sort information from
		 * @return {Object} Returns an object that contains local aggregate sort details
		 */
		_getSlotSortInfo(mappedSlot) {
			const slotDefinition = mappedSlot.getDefinition();
			const operationDefinition = slotDefinition.getAggregatedSortInfo();
			if (!operationDefinition) {
				return; //This slot does not support local sort
			}

			const mappedDataItem = mappedSlot.getDataItemList()[0];
			const mappedDataItemSort = mappedDataItem  && !mappedDataItem.hasDefaultSort() && mappedDataItem.getSort();
			if (!mappedDataItemSort) {
				return; //Cannot apply sort
			}

			if (!this._areRequiredSlotsForSortingMapped(operationDefinition)) {
				return; //Conditions for local sort not satisfied
			}

			const slots = this.visualization.getSlots();
			const groupSlot = slots.getSlot(operationDefinition.groupOnColumnId);
			const groupDataItems = groupSlot && groupSlot.getDataItemList();
			if (!groupDataItems || !groupDataItems.length) {
				return; //Group slot has no mapping - sort does not apply
			}

			const existingGroupSort = groupDataItems[0] && !groupDataItems[0].hasDefaultSort() && groupDataItems[0].getSort();
			if (existingGroupSort) {
				return; //Grouped slot's existing sort votes local sort
			}

			const groupDataItemIds = _.map(groupDataItems, dataItem => dataItem.getId());
			const aggregateDataItemId = mappedSlot.getDataItemList()[0].getId();

			// Secondary sort in each bin of the group. Only able to handle one slot in mappedSlotCondition of the vis definition.
			const mappedConditionSlot = slots.getSlot(operationDefinition.mappedSlotCondition[0]);

			return {
				order: mappedDataItemSort.type,
				groupDataItemIds: groupDataItemIds,
				aggregateDataItemId: aggregateDataItemId,
				// DataItems dropped in same slot are grouped to a new nested dataItem and the new data item is mapped to this slot. 
				// So the following works for single and nested data items.
				mappedSlotConditionDataItemUid: mappedConditionSlot.getDataItemList()[0].getId()
			};
		}

		/**
		 * @return {Object} Returns an object that contains local aggregate sort details from the first
		 * mapped slot that supports local client sort operation.
		 * If the visualization defines an extra step to aggregate and sort the result before rendering it.  This is
		 * needed to allow sorting on the ordinal slot when there's a color slot mapped
		 */
		_getAggregateSortInfo() {
			const mappedSlots = this.visualization.getSlots().getMappedSlotList();
			let sortInfo = null;
			for (let i = 0; i < mappedSlots.length && !sortInfo; i++) {
				sortInfo = this._getSlotSortInfo(mappedSlots[i]);
			}
			return sortInfo;
		}

		/**
		 * @param {Object} operationDefinition The clientSideAggregatedAndSortInfo from a slot definition.
		 * @return {Boolean} Returns true if there are required slots for sorting are mapped.
		 * We should only need to do aggregation and sorting on the client if the color slot is actually mapped.  This method
		 * will check if the 'mappedSlotCondition' (usually set to 'color') is fufilled.
		 */
		_areRequiredSlotsForSortingMapped(operationDefinition) {
			const slotConditions = operationDefinition.mappedSlotCondition;
			if (!slotConditions) {
				return true; //No conditions to verify, so slot is automatically applicable
			}

			const slots = this.visualization.getSlots();
			for (let i = 0; i < slotConditions.length; i++) {
				const requiredSlotId = slotConditions[i];
				const requiredSlot = slots.getSlot(requiredSlotId);
				if (!requiredSlot || !requiredSlot.getDataItemList().length) {
					return false;
				}
			}

			return true;
		}
	}
	return LocalAggregateSortInfo;
});
